<template>
    <div class="nevs-content">
        <div v-if="dailyReportPopup.show" class="nevs-popup-container">
            <div class="nevs-popup" style="max-height: 70vh; overflow-y: scroll">
                <div class="nevs-popup-text">
                    <h2>{{ dailyReportPopup.date }}</h2>
                    <table style="width: 100%; margin: 0;" class="report-table">
                        <thead>
                            <tr class="report-table-header-row">
                                <th style="text-align: left;">{{ $LANG.Get('labels.invoice') }}</th>
                                <th style="text-align: left;">{{ $LANG.Get('labels.paymentMethod') }}</th>
                                <th style="text-align: right;">{{ $LANG.Get('labels.amount') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(row, key) in dailyReportPopup.data" :key="key" class="report-table-data-row">
                            <td style="text-align: left;">{{ row.invoice_number }}</td>
                            <td style="text-align: left;">{{ $LANG.Get('paymentMethods.' + row.payment_method_id) }}</td>
                            <td style="text-align: right;">{{ row.amount_display }}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div class="nevs-popup-buttons">
                    <NevsButton @click="excelExportDetailed()"><i class="fa-solid fa-file-excel"></i> Excel</NevsButton>
                    <NevsButton @click="pdfExportDetailed()"><i class="fa-solid fa-file-pdf"></i> PDF</NevsButton>
                    <NevsButton class="success" @click="closeDailyReport()"><i class="fa-solid fa-xmark"></i> {{ $LANG.Get('buttons.close') }}</NevsButton>
                </div>
            </div>
        </div>
        <NevsCard>
            <NevsDateField :label="$LANG.Get('labels.dateFrom') + ':'" v-model="selectedDateFrom" :width="'300px;'"
                           style="float: left"></NevsDateField>
            <NevsDateField :label="$LANG.Get('labels.dateTo') + ':'" v-model="selectedDateTo" :width="'300px;'"
                           style="float: left"></NevsDateField>
            <span style="float: right; margin-right: 10px;"><NevsButton @click="excelExport"><i
                class="fa-solid fa-file-excel"></i></NevsButton></span>
            <div style="clear: both; height: 50px;"></div>
            <table class="report-table">
                <thead>
                    <tr class="report-table-header-row">
                        <th style="text-align: left;">{{ $LANG.Get('fields.date') }}</th>
                        <th style="text-align: right; width: 100px;">{{ $LANG.Get('paymentMethods.1') }}</th>
                        <th style="text-align: right; width: 100px;">{{ $LANG.Get('paymentMethods.2') }}</th>
                        <th style="text-align: right; width: 100px;">{{ $LANG.Get('paymentMethods.3') }}</th>
                        <th style="text-align: right; width: 100px;">{{ $LANG.Get('paymentMethods.4') }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                <tr v-for="(row, key) in report" :key="key" class="report-table-data-row">
                    <td>{{ row.date_display }}</td>
                    <td style="text-align: right;">{{ row.PM1_display }}</td>
                    <td style="text-align: right;">{{ row.PM2_display }}</td>
                    <td style="text-align: right;">{{ row.PM3_display }}</td>
                    <td style="text-align: right;">{{ row.PM4_display }}</td>
                    <td><NevsButton @click="openDailyReport(row.date, row.date_display)"><i class="fa-solid fa-file-lines"></i> {{ $LANG.Get('buttons.dailyReport') }}</NevsButton></td>
                </tr>
                </tbody>
                <tfoot v-if="report.length > 0">
                    <tr class="report-table-total-row">
                        <td>{{ $LANG.Get('fields.total') }}</td>
                        <td style="text-align: right;">{{ $HELPERS.FormatMoney(totals.PM1) }} {{ totals.currency }}</td>
                        <td style="text-align: right;">{{ $HELPERS.FormatMoney(totals.PM2) }} {{ totals.currency }}</td>
                        <td style="text-align: right;">{{ $HELPERS.FormatMoney(totals.PM3) }} {{ totals.currency }}</td>
                        <td style="text-align: right;">{{ $HELPERS.FormatMoney(totals.PM4) }} {{ totals.currency }}</td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </NevsCard>
    </div>
</template>

<script>
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import moment from "moment";

// The fixed conversion rate the kuna was replaced at, used to bring pre-2023 days into euro when a range
// covers both sides of the transition. Same rate the invoice and offer PDFs convert with.
const HRK_PER_EUR = 7.53450;

export default {
    name: "ModuleDailyReport",
    components: {NevsButton, NevsDateField, NevsCard},
    data() {
        return {
            loaded: false,
            selectedDateFrom: '',
            selectedDateTo: '',
            report: [],
            dailyReportPopup: {
                show: false,
                date: '',
                rawDate: '',
                data: []
            }
        }
    },
    computed: {
        // The row under the table: every payment method column summed over the days in range. The server
        // already converted each day's payments into that day's currency, so within one currency the amounts
        // add up as they are. Days before the 2023 euro transition are in HRK: a range that crosses it has
        // those converted to euro at the fixed rate, so the row stays a single number rather than adding two
        // currencies together. A range that is entirely HRK is left in HRK.
        totals() {
            let currencies = [];
            for (let row of this.report) {
                if (!currencies.includes(row.currency)) currencies.push(row.currency);
            }
            let mixed = currencies.length > 1;
            let totals = {
                currency: mixed ? 'EUR' : (currencies.length > 0 ? currencies[0] : ''),
                PM1: 0,
                PM2: 0,
                PM3: 0,
                PM4: 0
            };
            for (let row of this.report) {
                let rate = (mixed && row.currency === 'HRK') ? HRK_PER_EUR : 1;
                for (let key of ['PM1', 'PM2', 'PM3', 'PM4']) {
                    totals[key] += (row[key] ?? 0) / rate;
                }
            }
            return totals;
        }
    },
    watch: {
        selectedDateFrom() {
            this.refreshData();
        },
        selectedDateTo() {
            this.refreshData();
        }
    },
    methods: {
        closeDailyReport()
        {
            this.dailyReportPopup.show = false;
            let vm = this;
            this.$nextTick(()=>{
                vm.dailyReportPopup.data = [];
                vm.dailyReportPopup.date = '';
                vm.dailyReportPopup.rawDate = '';
            });
        },
        openDailyReport(date, dateDisplay)
        {
            let vm = this;
            this.$API.APICall('get', 'statistics/daily-report', {
                date: date
            }, (data, success) => {
                if (success) {
                    vm.dailyReportPopup.data = data;
                    vm.dailyReportPopup.date = dateDisplay;
                    vm.dailyReportPopup.rawDate = date;
                    vm.$nextTick(()=>{
                        vm.dailyReportPopup.show = true;
                    });
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        excelExportDetailed() {
            let vm = this;
            this.$API.APICall('get', 'statistics/daily-report-excel', {
                date: this.dailyReportPopup.rawDate
            }, (data, success) => {
                if (success) {
                    window.open(data.link);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        pdfExportDetailed() {
            let vm = this;
            this.$API.APICall('get', 'statistics/daily-report-pdf', {
                date: this.dailyReportPopup.rawDate
            }, (data, success) => {
                if (success) {
                    window.open(data.link);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        excelExport() {
            let vm = this;
            this.$API.APICall('get', 'statistics/daily-excel', {
                date_from: this.selectedDateFrom,
                date_to: this.selectedDateTo
            }, (data, success) => {
                if (success) {
                    window.open(data.link);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        refreshData() {
            let vm = this;
            this.$API.APICall('get', 'statistics/daily', {
                date_from: this.selectedDateFrom,
                date_to: this.selectedDateTo
            }, (data, success) => {
                if (success) {
                    vm.report = data;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
                vm.$nextTick(() => {
                    vm.loaded = true;
                });
            });
        }
    },
    mounted() {
        this.$store.commit('selectMenu', 'statistics');
        this.$store.commit('selectSubMenu', 'dailyReport');
        this.$store.commit('setBreadcrumbs', [
            {
                label: this.$LANG.Get('modules.dailyReport'),
                link: null
            }
        ]);

        this.selectedDateFrom = moment().add(-1, 'month').format('YYYY-MM-DD');
        this.selectedDateTo = moment().format('YYYY-MM-DD');
    }
}
</script>

<style scoped>
.report-table {
    border-collapse: collapse;
    margin: 10px;
}

.report-table td {
    padding: 5px;
}

.report-table-header-row th {
    padding: 5px;
    font-weight: bold;
    border-bottom: 2px grey solid;
}

.report-table-data-row td {
    border-bottom: 1px lightgray solid;
}

.report-table-total-row td {
    font-weight: bold;
    border-top: 2px grey solid;
}
</style>